import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import clsx from 'clsx'
import { store } from '../../stores/store'
import GameControls from '../../components/ui/controls/GameControls'
import ControlHintArrow from '../../components/ui/controls/ControlHintArrow'
import TabControls from '@/src/components/ui/controls/TabControls'
import ButtonIcon from '@/src/components/ui/buttons/ButtonIcon'
import IconPause from '@/src/components/ui/icons/IconPause'
import IconSettings from '@/src/components/ui/icons/IconSettings'
import { useRunState } from '../provider/useRunState'
import { useRestartRun } from '../../hooks/useRestartRun'
import PauseScreen from '@/src/components/screens/PauseScreen'
import { TutorialController } from '../tutorial/TutorialController'
import EndScreen from '@/src/components/screens/EndScreen'
import DisplayElement from '@/src/components/ui/gameElements/DisplayElement'
import PhaseTransitionPopUp from '@/src/components/screens/PhaseTransitionPopUp'
import { gameHudVariants } from '@/src/lib/motionVariants'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import ConfettiBurstOverlay from '@/src/components/ui/effects/ConfettiBurstOverlay'
import {
    useHintWooshSound,
    useSlideInWooshOnActive,
    playCountdownSound,
    playPopupOpenSound,
} from '@/src/game/audio/uiSounds'
import GameControlsStele from '@/src/components/ui/controls/GameControlsStele'
import TutorialOverlays from './TutorialOverlays'
import { subscribeToAppBackground } from '@/src/utils/appActivity'
import { dispatchConfettiBurst } from '@/src/components/ui/effects/confettiBurstEvent'

type GameHudProps = {
    onReturnToMenu?: () => void
    onReturnToConfigurator?: () => void
}

type HudPowerUpFxType = 'shield' | 'snap' | 'plane'
const PARENTS_BANNER_LEVEL_BLOCK_BY_REGION = {
    1: 'level1',
    2: 'level2',
    3: 'level3',
    4: 'level4',
} as const

function getCountdownImage(count: number) {
    switch (count) {
        case 1: return '/images/countdown/Countdown1.png'
        case 2: return '/images/countdown/Countdown2.png'
        case 3: return '/images/countdown/Countdown3.png'
        case 0: return '/images/countdown/Lauf!.png'
        default: return '/images/countdown/Lauf!.png'
    }
}

export const GameHud = observer(function GameHud({
    onReturnToMenu,
    onReturnToConfigurator,
}: GameHudProps) {
    const runState = useRunState()
    const restartRun = useRestartRun()
    const [resumeCountdown, setResumeCountdown] = useState<number | null>(null)
    const [isStartPhasePopupActive, setIsStartPhasePopupActive] = useState(false)
    const [activePowerUpFx, setActivePowerUpFx] = useState<HudPowerUpFxType | null>(null)
    const [powerUpFxKey, setPowerUpFxKey] = useState(0)
    const hudIntroCompleteRef = useRef(false)
    const previousShieldCollectCountRef = useRef(0)
    const previousShieldActiveRef = useRef(false)
    const previousSnapCollectCountRef = useRef(0)
    const previousPlaneCollectCountRef = useRef(0)
    const overlayFreezeResumePhaseRef = useRef<typeof runState.phase | null>(null)
    const previousPhaseRef = useRef(runState.phase)
    const previousBannerPhaseRef = useRef(runState.phase)
    const previousBannerRegionRef = useRef(runState.trackRegionState.activeRegion)
    const isCountdown = store.appFlowPhase === 'countdown'
    const isRun = store.appFlowPhase === 'run'
    const showGameOverlay = isCountdown || isRun
    const showHud = isRun
    const isStele = useSteleBreakpoint();
    const snapshot = runState.snapshot

    const handlePause = useCallback(() => {
        playPopupOpenSound()
        runState.pause()
    }, [runState])

    const handleResumeFromPause = useCallback(() => {
        playCountdownSound()
        setResumeCountdown(3)
    }, [])

    useEffect(() => {
        if (!isRun) {
            hudIntroCompleteRef.current = false
            setIsStartPhasePopupActive(false)
        }
    }, [isRun])

    useEffect(() => {
        if (isStele) return

        return subscribeToAppBackground(() => {
            setResumeCountdown(null)
        })
    }, [isStele])

    useEffect(() => {
        const previousPhase = previousPhaseRef.current
        const currentPhase = runState.phase

        if (previousPhase !== 'levelComplete' && currentPhase === 'levelComplete') {
            dispatchConfettiBurst()
        }

        previousPhaseRef.current = currentPhase
    }, [runState.phase])

    useEffect(() => {
        const currentPhase = runState.phase
        const currentRegion = runState.trackRegionState.activeRegion
        const phaseChanged = previousBannerPhaseRef.current !== currentPhase
        const regionChanged = previousBannerRegionRef.current !== currentRegion

        if (currentPhase === 'gameOver') {
            if (phaseChanged) {
                store.setParentsInfoBannerBlock('gameOver', { priority: 4 })
            }
        } else if (currentPhase === 'levelComplete' || currentPhase === 'endCard') {
            if (phaseChanged) {
                store.setParentsInfoBannerBlock('endgame', { priority: 3 })
            }
        } else if (
            currentPhase === 'running' ||
            currentPhase === 'levelTransition' ||
            currentPhase === 'paused'
        ) {
            if (phaseChanged || regionChanged) {
                store.setParentsInfoBannerBlock(PARENTS_BANNER_LEVEL_BLOCK_BY_REGION[currentRegion], { priority: 2 })
            }
        }

        previousBannerPhaseRef.current = currentPhase
        previousBannerRegionRef.current = currentRegion
    }, [runState.phase, runState.trackRegionState.activeRegion])

    useEffect(() => {
        if (resumeCountdown === null) return

        if (resumeCountdown <= 1) {
            const timeoutId = window.setTimeout(() => {
                setResumeCountdown(null)
                runState.start()
            }, 1000)

            return () => window.clearTimeout(timeoutId)
        }

        const timeoutId = window.setTimeout(() => {
            setResumeCountdown((current) => (current !== null ? current - 1 : current))
        }, 1000)

        return () => window.clearTimeout(timeoutId)
    }, [resumeCountdown, runState])

    const isWinSequence =
        runState.phase === 'levelComplete' ||
        runState.phase === 'endCard'
    const isLevelComplete = runState.phase === 'endCard'
    const isGameOver = runState.phase === 'gameOver'
    const isEndPhase = isLevelComplete || isGameOver
    const fadePauseButton = isGameOver || isWinSequence
    const showTouchControls =
        !isEndPhase &&
        (!isCountdown || (isRun && runState.phase === 'running'))
    const showPauseScreen =
        runState.phase === 'paused' &&
        resumeCountdown === null &&
        !store.powerUpTutorial.isOverlayVisible
    const showHudContent =
        showHud &&
        !isGameOver &&
        !isWinSequence &&
        runState.phase !== 'paused' &&
        !isStartPhasePopupActive
    const isPhaseTransition = runState.trackRegionState.activeTransition
    const hudAnimateState = isPhaseTransition ? 'hidden' : 'visible'

    useSlideInWooshOnActive(showGameOverlay && showHudContent && hudAnimateState === 'visible')

    const isShieldActive = snapshot.shieldActive
    const shieldCollectCount = snapshot.runEndStats.shieldPowerUpsCollected
    const snapCollectCount = snapshot.runEndStats.batteryPowerUpsCollected
    const planeCollectCount = snapshot.runEndStats.planePowerUpsCollected

    useEffect(() => {
        const shieldCollected = shieldCollectCount > previousShieldCollectCountRef.current
        const snapCollected = snapCollectCount > previousSnapCollectCountRef.current
        const planeCollected = planeCollectCount > previousPlaneCollectCountRef.current

        if (shieldCollected && !previousShieldActiveRef.current) {
            setPowerUpFxKey((current) => current + 1)
            setActivePowerUpFx('shield')
        }

        if (snapCollected) {
            setPowerUpFxKey((current) => current + 1)
            setActivePowerUpFx('snap')
        }

        if (planeCollected) {
            setPowerUpFxKey((current) => current + 1)
            setActivePowerUpFx('plane')
        }

        if (store.tutorialEnabled) {
            if (shieldCollected) store.powerUpTutorial.requestOverlay('shield')
            if (snapCollected) store.powerUpTutorial.requestOverlay('snap')
            if (planeCollected) store.powerUpTutorial.requestOverlay('plane')
        }

        previousShieldCollectCountRef.current = shieldCollectCount
        previousShieldActiveRef.current = isShieldActive
        previousSnapCollectCountRef.current = snapCollectCount
        previousPlaneCollectCountRef.current = planeCollectCount
    }, [isShieldActive, planeCollectCount, shieldCollectCount, snapCollectCount])

    const powerUpTutorialVariant = store.powerUpTutorial.activeVariant

    useEffect(() => {
        if (powerUpTutorialVariant) {
            const canFreeze =
                runState.phase === 'running' || runState.phase === 'levelTransition'

            if (overlayFreezeResumePhaseRef.current === null && canFreeze) {
                overlayFreezeResumePhaseRef.current = runState.phase
                runState.pause()
            }

            return
        }

        if (overlayFreezeResumePhaseRef.current !== null) {
            if (runState.phase === 'paused') {
                runState.setPhase(overlayFreezeResumePhaseRef.current)
            }

            overlayFreezeResumePhaseRef.current = null
        }
    }, [powerUpTutorialVariant, runState, runState.phase])

    return (
        <div className='z-50 absolute inset-0 pointer-events-none'>
            <ConfettiBurstOverlay className='z-5' />
            {!showGameOverlay ? null : (
                <>
                    <TutorialController />
                    <TutorialHintOverlay
                        showGameOverlay={showGameOverlay}
                        showTouchControls={showTouchControls}
                    />


                    <AnimatePresence mode='wait'>
                        {runState.phase === 'levelComplete' &&
                            <motion.div
                                className='z-10 absolute inset-0 flex justify-center items-center overflow-hidden pointer-events-none'
                                initial={{ opacity: 0, scale: 0, y: '15%' }}
                                animate={{ opacity: 100, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0, y: '15%' }}
                            >
                                <img src='/images/countdown/Geschafft.png' className='w-[320px] stele:w-7xl h-auto stele:h-auto' />
                            </motion.div>
                        }
                    </AnimatePresence>

                    <PhaseTransitionPopUp onStartPhaseActiveChange={setIsStartPhasePopupActive} />

                    {isStele ?
                        <GameControlsStele
                            visible={showTouchControls}
                            onJump={() => runState.startJump()}
                            onMoveLeft={() => runState.moveLane(-1)}
                            onMoveRight={() => runState.moveLane(1)}
                        />
                        :
                        <GameControls
                            visible={showTouchControls}
                            onJump={() => runState.startJump()}
                            onMoveLeft={() => runState.moveLane(-1)}
                            onMoveRight={() => runState.moveLane(1)}
                        />
                    }

                    <motion.div
                        key={powerUpFxKey}
                        initial={{ opacity: 0, scale: 0.2, y: 0 }}
                        animate={
                            activePowerUpFx
                                ? {
                                    opacity: [0, 0, 1, 1, 1, 1, 0],
                                    scale: [0.2, 1.18, 1, 1, 0.46],
                                    y: activePowerUpFx === 'shield'
                                        ? [0, 0, 0, isStele ? '-28%' : '-38%']
                                        : [0, 0, 0, '28%'],
                                }
                                : { opacity: 0, scale: 0.2, y: 0 }
                        }
                        transition={{
                            duration: activePowerUpFx === 'shield' ? 1.2 : 1,
                            // times: [0, 0.18, 0, 4, 0.62, 1],
                            ease: 'easeOut',
                        }}
                        onAnimationComplete={() => {
                            setActivePowerUpFx(null)
                        }}
                        className='z-40 absolute inset-0 flex justify-center items-center'
                    >
                        <div className={clsx(
                            'flex items-center w-full max-w-100 h-full',
                            snapshot.laneIndex === 0 && 'justify-start',
                            snapshot.laneIndex === 1 && 'justify-center',
                            snapshot.laneIndex === 2 && 'justify-end'
                        )}>
                            {activePowerUpFx &&
                                <img
                                    src={
                                        activePowerUpFx === 'shield'
                                            ? '/images/powerups/PowerUp_Shield.png'
                                            : activePowerUpFx === 'snap'
                                                ? '/images/powerups/PowerUp_Batterie.png'
                                                : '/images/powerups/PowerUp_Star.png'
                                    }
                                    className={clsx(
                                        'w-24 h-24',
                                        'stele:w-96 stele:h-96',

                                    )}
                                />
                            }
                        </div>
                    </motion.div>

                    {showHud && !store.continueOnPhoneOpen ? (
                        <>
                            {isStele ?
                                <div
                                    className={clsx(
                                        'bottom-0 z-30 absolute flex justify-center items-center w-full transition-opacity duration-500 ease-out',
                                        fadePauseButton
                                            ? 'opacity-0 pointer-events-none'
                                            : 'opacity-100',
                                    )}
                                >
                                    <div className='relative flex justify-center items-center w-11.25 stele:w-76'>
                                        <svg width={isStele ? '64' : '16'} height={isStele ? '64' : '16'} viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg' className='bottom-0 -left-4 stele:left-2.5 z-50 absolute'>
                                            <path d='M0 16C8.83656 16 16 8.83656 16 0V16H0Z' fill='#C9E3F7' />
                                        </svg>
                                        <TabControls position='midBottom'>
                                            <ButtonIcon icon={<IconPause width='100%' height='100%' />} color='orange' variant='tab' onClick={handlePause} />
                                        </TabControls>
                                        <svg width={isStele ? '64' : '16'} height={isStele ? '64' : '16'} viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg' className='-right-4 stele:right-2.5 bottom-0 z-50 absolute'>
                                            <path d='M16 16C7.16344 16 0 8.83656 0 0V16H16Z' fill='#C9E3F7' />
                                        </svg>
                                    </div>
                                </div>
                                :
                                <div
                                    className={clsx(
                                        'z-30 relative transition-opacity duration-500 ease-out',
                                        fadePauseButton
                                            ? 'opacity-0 pointer-events-none'
                                            : 'opacity-100',
                                    )}
                                >
                                    <TabControls position='topRight'>
                                        <ButtonIcon icon={<IconPause width='100%' height='100%' />} color='orange' variant='tab' onClick={handlePause} />
                                    </TabControls>
                                </div>
                            }

                        </>
                    ) : null}


                    {showPauseScreen ? (
                        <>
                            <PauseScreen onResume={handleResumeFromPause} />
                            {!isStele ? (
                                <TabControls>
                                    <ButtonIcon
                                        color='blue'
                                        icon={<IconSettings width='100%' height='100%' />}
                                        onClick={store.openSettings}
                                    />
                                </TabControls>
                            ) : null}
                        </>
                    ) : null}

                    {resumeCountdown !== null ? (
                        <div className='z-40 absolute inset-0 flex justify-center items-center bg-black/20'>
                            <img
                                src={getCountdownImage(resumeCountdown)}
                                className='w-40 stele:w-160 h-40 stele:h-160'
                                alt='countdown number'
                            />
                        </div>
                    ) : null}


                    {isEndPhase ? (
                        <EndScreen
                            isLevelComplete={isLevelComplete}
                            showLevelCompleteUi={
                                runState.levelCompleteCardAnimationComplete
                            }
                            isGameOver={isGameOver}
                            clampedDistance={Math.floor(store.displayTrackDistance)}
                            levelTargetDistance={Math.floor(store.displayTrackDistance)}
                            runStats={runState.snapshot.runEndStats}
                            onReplay={() => restartRun('countdown')}
                            onOpenEditor={onReturnToConfigurator ?? (() => { })}
                            onEditAppearance={() => restartRun('configurator', { withResetTransition: true })}
                        />
                    ) : null}



                    {showHudContent ? (
                        <>
                            <motion.div
                                className='w-full'
                                custom={isStele ? -600 : -150}
                                initial={hudIntroCompleteRef.current ? false : 'hidden'}
                                animate={hudAnimateState}
                                variants={gameHudVariants}
                                onAnimationComplete={() => {
                                    if (!isPhaseTransition) {
                                        hudIntroCompleteRef.current = true
                                    }
                                }}
                            >
                                <DisplayElement />
                            </motion.div>

                            {/* <div className='right-4 bottom-4 z-50 absolute flex gap-2 pointer-events-auto'>
                        {onReturnToMenu ? (
                            <button
                                type='button'
                                onClick={onReturnToMenu}
                                className='bg-black/70 px-4 py-2 rounded-full font-grandstander text-white'
                            >
                                Menü
                            </button>
                        ) : null}

                        {onReturnToConfigurator ? (
                            <button
                                type='button'
                                onClick={onReturnToConfigurator}
                                className='bg-black/70 px-4 py-2 rounded-full font-grandstander text-white'
                            >
                                Aussehen
                            </button>
                        ) : null}
                    </div> */}
                        </>
                    ) : null}

                    <TutorialOverlays />
                </>
            )}
        </div>
    )
})

const TutorialHintOverlay = observer(function TutorialHintOverlay({
    showGameOverlay,
    showTouchControls,
}: {
    showGameOverlay: boolean
    showTouchControls: boolean
}) {
    const showJumpHint = showGameOverlay && store.isTutorialHintDisplayed('jump')
    const showLeftHint = showGameOverlay && store.isTutorialHintDisplayed('left')
    const showRightHint = showGameOverlay && store.isTutorialHintDisplayed('right')
    const showSnapHint = showGameOverlay && store.isTutorialHintDisplayed('snap')

    useHintWooshSound(showJumpHint)
    useHintWooshSound(showLeftHint)
    useHintWooshSound(showRightHint)
    useHintWooshSound(showSnapHint)

    return (
        <>
            {showJumpHint && showTouchControls ? (
                <ControlHintArrow side='up' />
            ) : null}
            {showLeftHint && showTouchControls ? (
                <ControlHintArrow side='left' />
            ) : null}
            {showRightHint && showTouchControls ? (
                <ControlHintArrow side='right' />
            ) : null}
        </>
    )
})
